/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import type { DivProps } from 'typings';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDText, { UDTextType, UDTextVariant } from 'ud-ui/components/text';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import * as S from './styles';

const SINGLE_LINE = 'Съешь ещё этих мягких булок 0123456789';
const PARAGRAPH =
  'Расписание матчей чемпионата обновляется автоматически: результаты, составы команд ' +
  'и таблица группы пересчитываются сразу после завершения игры.';

const LEGACY_ROWS: { type: UDTextType; spec: string }[] = [
  { type: 'title-huge', spec: 'Display 400 · 42/50' },
  { type: 'title-large', spec: 'Display 400 · 34/41' },
  { type: 'title-1', spec: 'Display 400 · 28/34' },
  { type: 'title-3', spec: 'Display 400 · 20/25' },
  { type: 'headline', spec: 'Text 600 · 17/22' },
  { type: 'body-text', spec: 'Display 400 · 17/22' },
  { type: 'callout', spec: 'Display 400 · 16/21' },
  { type: 'subhead', spec: 'Display 400 · 15/20' },
  { type: 'footnote', spec: 'Display 400 · 13/18' },
  { type: 'caption-1', spec: 'Display 400 · 12/16' },
  { type: 'caption-2', spec: 'Display 400 · 11/13' },
  { type: 'tip', spec: 'Display 400 · 10/12' },
];

const SCALE_ROWS: { variant: UDTextVariant; spec: string }[] = [
  { variant: 'title-1', spec: 'Text 700 · 36/26' },
  { variant: 'title-2', spec: 'Text 400 · 28/100%' },
  { variant: 'title-3', spec: 'Display 600 · 20/100%' },
  { variant: 'title-4', spec: 'Text 400 · 15/100% · ls 1' },
  { variant: 'title-4-bold', spec: 'Text 700 · 15/100%' },
  { variant: 'title-5', spec: 'Display 600 · 14/100% · upper' },
  { variant: 'title-6', spec: 'Text 400 · 10/100%' },
  { variant: 'title-6-bold', spec: 'Text 700 · 10/100%' },
  { variant: 'subtitle-1', spec: 'Display 500 · 12/100%' },
  { variant: 'subtitle-1-bold', spec: 'Display 700 · 12/100%' },
  { variant: 'subtitle-2', spec: 'Text 400 · 12/16 — ВНЕ ДИЗАЙНА' },
  { variant: 'subtitle-2-bold', spec: 'Text 700 · 12/100%' },
  { variant: 'subtitle-4', spec: 'Text 400 · 10/100% (= title-6)' },
  { variant: 'body-1', spec: 'Display 700 · 16/100%' },
  { variant: 'body-2', spec: 'Display 400 · 15/100%' },
  { variant: 'body-3', spec: 'Display 500 · 15/100%' },
  { variant: 'text-1', spec: 'Display 400 · 16/100%' },
  { variant: 'text-2', spec: 'Display 400 · 14/100%' },
  { variant: 'text-2-bold', spec: 'Display 700 · 14/100%' },
  { variant: 'text-3', spec: 'Text 700 · 14/100%' },
];

interface SampleRowProps {
  name: string;
  spec: string;
  children: React.ReactNode;
}

function SampleRow(props: SampleRowProps) {
  const { name, spec, children } = props;

  return (
    <S.Row>
      <S.RowHead>
        <S.RowName>{name}</S.RowName>
        <S.RowSpec>{spec}</S.RowSpec>
      </S.RowHead>
      {children}
    </S.Row>
  );
}

interface IProps extends DivProps {}

function TypographyPage(props: IProps) {
  const { ...rest } = props;
  const { setHeaderContent } = useMainLayout();

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Типографика</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  return (
    <S.MainBlock {...rest}>
      <S.Intro>
        Витрина двух шкал, живущих в проекте одновременно на время миграции IMGM-1236. Имена вариантов в колонках совпадают, но
        означают разные стили — сравнивать нужно по спецификации под именем, а не по имени. Целевая — правая колонка: только она
        соответствует макету. Легаси-шкала уезжает по модулям в Фазе 3, после чего левая колонка исчезнет.
      </S.Intro>

      <S.Columns>
        <S.Column>
          <S.ColumnHead>
            <S.ColumnTitle>Легаси-шкала · 12 вариантов</S.ColumnTitle>
            <S.ColumnSource>.text — src/styles/typography.scss</S.ColumnSource>
          </S.ColumnHead>

          {LEGACY_ROWS.map(({ type, spec }) => (
            <SampleRow key={type} name={type} spec={spec}>
              <S.Sample>
                <S.SampleLabel>строка</S.SampleLabel>
                <UDText type={type}>{SINGLE_LINE}</UDText>
              </S.Sample>
              <S.Sample>
                <S.SampleLabel>абзац</S.SampleLabel>
                <UDText type={type}>{PARAGRAPH}</UDText>
              </S.Sample>
            </SampleRow>
          ))}
        </S.Column>

        <S.Column>
          <S.ColumnHead>
            <S.ColumnTitle>Целевая шкала · 20 вариантов</S.ColumnTitle>
            <S.ColumnSource>.typo — src/styles/typography-scale.css</S.ColumnSource>
          </S.ColumnHead>

          {SCALE_ROWS.map(({ variant, spec }) => (
            <SampleRow key={variant} name={variant} spec={spec}>
              <S.Sample>
                <S.SampleLabel>строка</S.SampleLabel>
                <UDText variant={variant}>{SINGLE_LINE}</UDText>
              </S.Sample>
              <S.Sample>
                <S.SampleLabel>абзац</S.SampleLabel>
                <UDText variant={variant}>{PARAGRAPH}</UDText>
              </S.Sample>
              <S.Sample>
                <S.SampleLabel>абзац + .relaxed</S.SampleLabel>
                <UDText variant={variant} relaxed>
                  {PARAGRAPH}
                </UDText>
              </S.Sample>
            </SampleRow>
          ))}
        </S.Column>
      </S.Columns>
    </S.MainBlock>
  );
}

export default TypographyPage;
